/**
 * Centralized Routing Debug Logger
 * 
 * Provides structured logging for all routing decisions, including redirects and access denials.
 * Tracks session cookies, authorization failures, same-origin policy routing, and cookie domain issues.
 */

import logger from '@/lib/logging';
import { getCorrelationId } from '@/lib/logging';

/**
 * Routing decision types
 */
export type RoutingDecision = 
  | 'allow'
  | 'redirect'
  | 'deny'
  | 'loading';

/**
 * Redirect destinations
 */
export type RedirectDestination = 
  | '/'
  | '/login'
  | '/signup-required'
  | '/welcome'
  | string;

/**
 * Redirect reasons
 */
export type RedirectReason =
  | 'not_authenticated'
  | 'missing_role'
  | 'insufficient_permissions'
  | 'no_access'
  | 'invalid_cookie'
  | 'expired_cookie'
  | 'cookie_domain_mismatch'
  | 'missing_cookie'
  | 'authorization_failed'
  | 'unknown';

/**
 * Cookie information structure
 */
export interface CookieInfo {
  present: boolean;
  name?: string;
  domain?: string;
  path?: string;
  secure?: boolean;
  sameSite?: string;
  httpOnly?: boolean;
  verificationResult?: 'valid' | 'invalid' | 'expired' | 'domain_mismatch' | 'not_verified';
  verificationError?: string;
}

/**
 * Request context information
 */
export interface RequestContext {
  origin?: string;
  hostname?: string;
  protocol?: string;
  referer?: string;
  userAgent?: string;
  path?: string;
  method?: string;
  isFirebaseHosting?: boolean;
  isAppHosting?: boolean;
  isLocalhost?: boolean;
  routingMethod?: 'rewrite' | 'direct' | 'local';
}

/**
 * Authentication state information
 */
export interface AuthState {
  authenticated: boolean;
  userId?: string;
  userEmail?: string;
  isGuest?: boolean;
  roles?: string[];
  abilities?: string[];
  authMethod?: 'cookie' | 'token' | 'none';
}

/**
 * Routing decision log entry
 */
export interface RoutingDecisionLog {
  correlationId: string;
  timestamp: string;
  decision: RoutingDecision;
  redirectDestination?: RedirectDestination;
  redirectReason?: RedirectReason;
  requestContext: RequestContext;
  cookieInfo: CookieInfo;
  authState: AuthState;
  additionalInfo?: Record<string, any>;
}

/**
 * Log a routing decision
 */
export function logRoutingDecision(
  decision: RoutingDecision,
  options: {
    redirectDestination?: RedirectDestination;
    redirectReason?: RedirectReason;
    requestContext?: Partial<RequestContext>;
    cookieInfo?: Partial<CookieInfo>;
    authState?: Partial<AuthState>;
    additionalInfo?: Record<string, any>;
  } = {}
): void {
  const correlationId = getCorrelationId();
  const timestamp = new Date().toISOString();

  // Build request context
  const requestContext: RequestContext = {
    origin: typeof window !== 'undefined' ? window.location.origin : undefined,
    hostname: typeof window !== 'undefined' ? window.location.hostname : undefined,
    protocol: typeof window !== 'undefined' ? window.location.protocol : undefined,
    referer: typeof document !== 'undefined' ? document.referrer : undefined,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    path: typeof window !== 'undefined' ? window.location.pathname : undefined,
    ...options.requestContext,
  };

  // Detect routing method
  // Firebase Hosting = static hosting (.web.app, .firebaseapp.com) - rewrites /api/* to App Hosting
  // App Hosting = Cloud Run backend (.hosted.app) - direct access
  if (requestContext.hostname) {
    requestContext.isFirebaseHosting = 
      requestContext.hostname.endsWith('.web.app') || 
      requestContext.hostname.endsWith('.firebaseapp.com');
    requestContext.isAppHosting = requestContext.hostname.includes('.hosted.app');
    requestContext.isLocalhost = 
      requestContext.hostname === 'localhost' || 
      requestContext.hostname === '127.0.0.1';

    if (requestContext.isFirebaseHosting) {
      requestContext.routingMethod = 'rewrite'; // Firebase Hosting rewrites to App Hosting
    } else if (requestContext.isAppHosting) {
      requestContext.routingMethod = 'direct'; // Direct access to App Hosting (Cloud Run)
    } else if (requestContext.isLocalhost) {
      requestContext.routingMethod = 'local'; // Local development
    }
  }

  // Build cookie info
  const cookieInfo: CookieInfo = {
    present: false,
    ...options.cookieInfo,
  };

  // Build auth state
  const authState: AuthState = {
    authenticated: false,
    ...options.authState,
  };

  // Create log entry
  const logEntry: RoutingDecisionLog = {
    correlationId,
    timestamp,
    decision,
    redirectDestination: options.redirectDestination,
    redirectReason: options.redirectReason,
    requestContext,
    cookieInfo,
    authState,
    additionalInfo: options.additionalInfo,
  };

  // Log based on decision type
  if (decision === 'redirect' && options.redirectDestination === '/signup-required') {
    logger.error('🔴 [ROUTING] Redirect to /signup-required', {
      category: 'auth',
      correlationId,
      redirectReason: options.redirectReason,
      authState,
      cookieInfo,
      requestContext,
      ...options.additionalInfo,
    });
  } else if (decision === 'redirect') {
    logger.warn('🟡 [ROUTING] Redirect decision', {
      category: 'auth',
      correlationId,
      redirectDestination: options.redirectDestination,
      redirectReason: options.redirectReason,
      authState,
      cookieInfo,
      requestContext,
      ...options.additionalInfo,
    });
  } else if (decision === 'deny') {
    logger.warn('🟠 [ROUTING] Access denied', {
      category: 'auth',
      correlationId,
      redirectReason: options.redirectReason,
      authState,
      cookieInfo,
      requestContext,
      ...options.additionalInfo,
    });
  } else {
    logger.debug('🟢 [ROUTING] Routing decision', {
      category: 'auth',
      correlationId,
      decision,
      authState,
      cookieInfo,
      requestContext,
      ...options.additionalInfo,
    });
  }
}

/**
 * Log cookie information
 */
export function logCookieInfo(
  cookieInfo: CookieInfo,
  requestContext?: Partial<RequestContext>
): void {
  const correlationId = getCorrelationId();

  if (!cookieInfo.present) {
    logger.debug('🍪 [ROUTING] No session cookie received', {
      category: 'auth',
      correlationId,
      requestContext,
    });
  } else {
    logger.debug('🍪 [ROUTING] Session cookie received', {
      category: 'auth',
      correlationId,
      cookieInfo,
      requestContext,
    });

    // Check for domain mismatches
    if (requestContext?.hostname && cookieInfo.domain) {
      const hostnameMatches = 
        requestContext.hostname === cookieInfo.domain ||
        requestContext.hostname.endsWith(`.${cookieInfo.domain}`) ||
        cookieInfo.domain === `.${requestContext.hostname}`;

      if (!hostnameMatches) {
        logger.warn('⚠️ [ROUTING] Cookie domain mismatch detected', {
          category: 'auth',
          correlationId,
          requestHostname: requestContext.hostname,
          cookieDomain: cookieInfo.domain,
          requestContext,
        });
      }
    }
  }
}

/**
 * Log authorization failure
 */
export function logAuthorizationFailure(
  reason: RedirectReason,
  options: {
    requestContext?: Partial<RequestContext>;
    cookieInfo?: Partial<CookieInfo>;
    authState?: Partial<AuthState>;
    additionalInfo?: Record<string, any>;
  } = {}
): void {
  const correlationId = getCorrelationId();

  logger.error('❌ [ROUTING] Authorization failure', {
    category: 'auth',
    correlationId,
    reason,
    authState: options.authState,
    cookieInfo: options.cookieInfo,
    requestContext: options.requestContext,
    ...options.additionalInfo,
  });
}

/**
 * Log same-origin policy and reverse proxy routing
 */
export function logRoutingMethod(
  requestContext: RequestContext,
  detectedMethod: 'rewrite' | 'direct' | 'local' | 'unknown'
): void {
  const correlationId = getCorrelationId();

  logger.debug('🔄 [ROUTING] Routing method detected', {
    category: 'auth',
    correlationId,
    method: detectedMethod,
    requestContext,
  });

  // Warn if direct app hosting calls are detected on client
  if (typeof window !== 'undefined' && detectedMethod === 'direct' && requestContext.isFirebaseHosting) {
    logger.warn('⚠️ [ROUTING] Potential direct app hosting call detected', {
      category: 'auth',
      correlationId,
      message: 'Client is on Firebase Hosting but making direct calls to App Hosting. Should use reverse proxy via rewrites.',
      requestContext,
    });
  }
}

/**
 * Export debug information for troubleshooting
 */
export function exportRoutingDebugInfo(): RoutingDecisionLog[] {
  // In a real implementation, this could collect recent routing decisions
  // For now, return empty array as this is primarily for logging
  return [];
}
